/**
 * One row of the Supplier Performance table — a supplier as a whole, or (the
 * `site` variant) that supplier at one customer site.
 *
 * Split out of PerformanceTable (which was past the 500-line limit). The
 * frozen-column `left` offsets come from `frozenColumns` in
 * PerformanceTableHeader.
 *
 * Customer review 11 Sep (item 3/4): the top-level row is the supplier over
 * every customer site in view. Its Customer cell says so ("All sites (N)", the
 * list and each site's supplier code on hover), and a chevron opens one row
 * per site beneath it — the old portal's group → supplier → site model
 * (the reviewer's m04b). A site row's count and money cells open the same
 * drill-downs as the supplier row, scoped to that one site (the row passes
 * its `site`; useDrilldownModal lists that site only), so each modal lists
 * exactly the records behind the figure clicked — Customer review 11 Sep
 * (stream I, from B).
 */
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { SupplierProfile, SupplierRatingThreshold, SupplierCustomerSite, OTIFGradeThreshold } from '../../lib/api';
import { toDeliveryBands } from '../../hooks/useOTIFBands';
import { getDeliveryColor } from '../../components/dashboard/SiteKPIComponents';
import { roundHalfUp } from '../../lib/kpiPercent';
import {
  siteCodeLabel,
  customerScopeLabel,
  formatCurrency,
  getGradeInfo,
  getRatingStyle,
  isNARating,
} from './utils';
import { selfRowOf } from './selfPerformance';
import { frozenColumns } from './PerformanceTableHeader';
import { DrillSite } from './types';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';

// `site` (last): set by a per-site breakdown row — the drill lists that site only.
export interface RowCallbacks {
  // CR-6134: Added otdStatus parameter for Ontime/Late/Early GRN drilldowns
  onOpenGRNModal: (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    otdStatus?: 'on_time' | 'late' | 'early',
    site?: DrillSite,
  ) => void;
  onOpenNCRModal: (supplierId: number, supplierName: string, filterType?: string, value?: number, site?: DrillSite) => void;
  onOpenPOLineModal: (
    supplierId: number,
    supplierName: string,
    filterType?: string,
    value?: number,
    additionalFilters?: { status?: string; on_hold?: boolean },
    site?: DrillSite,
  ) => void;
}

interface PerformanceTableRowProps extends RowCallbacks {
  supplier: SupplierProfile;
  /** Grade bands for THIS row (the row's customer's on the supplier page). */
  thresholds: SupplierRatingThreshold[];
  /**
   * CR-6269: the viewer's OTIF Grade Thresholds. The OTIF % cell is coloured
   * from these — the same bands the dashboard tiles use — and stays neutral
   * when the viewer has none (a supplier is graded per customer).
   */
  otifBands?: OTIFGradeThreshold[];
  currency: string;
  viewerIsSupplier: boolean;
  showSupplierSite: boolean;
  /**
   * `site`: the supplier at one customer site, under its supplier row.
   * `code`: CR-7288 — one supplier code at that site, under the site row.
   */
  variant?: 'supplier' | 'site' | 'code';
  /** The site a `site` or `code` row stands for. */
  site?: SupplierCustomerSite;
  /** CR-7288: what a `code` row calls itself, including the no-code bucket. */
  codeLabelOverride?: string;
  /** CR-7288: narrows this row's drill-downs to one code. */
  drillCode?: string;
  /** CR-7288: this row IS the `(no code)` bucket; its drills ask for that. */
  drillNoCode?: boolean;
  /** The row covers several sites and can open its per-site breakdown. */
  expandable?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

export function PerformanceTableRow({
  supplier,
  thresholds,
  otifBands = [],
  currency,
  viewerIsSupplier,
  showSupplierSite,
  variant = 'supplier',
  site,
  codeLabelOverride,
  drillCode,
  drillNoCode,
  expandable = false,
  expanded = false,
  onToggleExpand,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableRowProps) {
  // CR-4547: read the flag here rather than thread it through the three row
  // tiers (supplier / site / code), so no tier can render a cell the header lacks.
  const { warehouse: showWarehouse } = useFilterVisibility();
  const self = selfRowOf(supplier);
  const isCodeRow = variant === 'code';
  // A code row is a site row one level in: same frozen-cell layout, same
  // shading, deeper indent. Keeping them one branch stops the two drifting.
  const isSiteRow = variant === 'site' || isCodeRow;
  const rowId = self?.self_key ?? supplier.company;
  const rowName = self?.customer_name ?? supplier.company_name;
  const { customerLeft, ratingLeft } = frozenColumns(viewerIsSupplier, showSupplierSite);
  const scope = customerScopeLabel(supplier.customer_sites);
  const singleSite = supplier.customer_sites?.length === 1 ? supplier.customer_sites[0] : undefined;
  // Solid, not a translucent tint: the frozen cells sit over the scrolled
  // columns, and a see-through background let those numbers bleed through.
  const rowBg = isSiteRow ? 'bg-slate-50 dark:bg-slate-900' : 'bg-background';
  const money = (value: string | number | null | undefined) =>
    formatCurrency(value == null ? null : String(value), supplier.display_currency || currency);
  // The drill scope a site row adds (undefined on a supplier row).
  const drillSite: DrillSite | undefined = isSiteRow && site
    ? { id: site.id, name: site.name, code: drillCode, noCode: drillNoCode }
    : undefined;
  const at = drillSite ? ` at ${drillSite.name}` : '';

  // A drillable count — on a site row it opens that site's records only.
  const count = (
    value: number | undefined,
    className: string,
    title: string,
    open: () => void,
  ) => (
    <button onClick={open} className={`text-sm ${className} hover:underline cursor-pointer`} title={`${title}${at}`}>
      {(value || 0).toLocaleString()}
    </button>
  );

  // CR-7288: null (not 0) means the row cannot answer for CO2 - a code row,
  // because CO2 is captured per shipment and a shipment can span codes.
  // 0 still means "none captured", which is a different statement.
  const co2Unknown = supplier.carbon_emission_asn_count === null;
  const co2Count = supplier.carbon_emission_asn_count ?? 0;

  return (
    <tr
      className={`border-b transition-colors ${isSiteRow ? 'bg-slate-50 dark:bg-slate-900' : 'hover:bg-muted/30'}`}
      data-testid={isSiteRow ? `site-row-${supplier.company}-${site?.id}` : `supplier-row-${supplier.company}`}
    >
      {!viewerIsSupplier && (
        // Supplier Name - Frozen (with the breakdown chevron)
        <td className={`p-3 sticky left-0 ${rowBg} z-10`}>
          {isSiteRow ? (
            <div className={`flex items-start gap-2 ${isCodeRow ? 'pl-14' : 'pl-7'}`}>
              {/* CR-7288: a site holding more than one code can be split again.
                  The spacer keeps every site row's text on the same left edge
                  whether or not it has a chevron. */}
              {expandable ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Hide ${site?.name} by supplier code` : `Show ${site?.name} by supplier code`}
                  title={expanded ? 'Hide the per-code breakdown' : 'Show performance for each supplier code at this site'}
                  className="mt-0.5 h-5 w-5 shrink-0 inline-flex items-center justify-center rounded hover:bg-muted"
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              <div>
                {/* CR-7288, Aaron 18 Sep: a CODE row is identified by its code
                    and nothing else — the supplier name is already on the two
                    rows above it, so repeating it here is noise. A SITE row
                    keeps the supplier name, and names its code only when it
                    holds exactly one AND does not also carry uncoded work
                    (see `siteCodeLabel` — round 2). */}
                {isCodeRow ? (
                  <div className="text-sm text-muted-foreground">{codeLabelOverride}</div>
                ) : (
                  <>
                    <div className="text-sm text-muted-foreground">{supplier.company_name}</div>
                    {siteCodeLabel(site ?? {}) && (
                      <div className="text-xs text-muted-foreground">
                        {siteCodeLabel(site ?? {})}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {expandable ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Hide ${supplier.company_name} by customer site` : `Show ${supplier.company_name} by customer site`}
                  title={expanded ? 'Hide the per-site breakdown' : 'Show performance at each customer site'}
                  className="h-5 w-5 shrink-0 inline-flex items-center justify-center rounded hover:bg-muted"
                >
                  {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              ) : (
                <span className="w-5 shrink-0" />
              )}
              {supplier.is_preferred && (
                <span className="text-yellow-500" title="Strategic Supplier">
                  ★
                </span>
              )}
              <div className="font-medium text-sm">{supplier.company_name}</div>
            </div>
          )}
        </td>
      )}

      {/* Q8: at GROUP view a supplier's rows span sibling sites — show which. */}
      {showSupplierSite && (
        <td className={`p-3 sticky left-0 ${rowBg} z-10`}>
          <div className="font-medium text-sm">{self?.supplier_site_name}</div>
          {self?.is_own_site === false && (
            <div className="text-xs text-muted-foreground">sister site</div>
          )}
        </td>
      )}
      {/* Customer - Frozen. SI-16: on the supplier page this is the row's
          customer (name, group beneath). */}
      <td className={`p-3 sticky ${rowBg} z-10`} style={{ left: `${customerLeft}px` }}>
        {viewerIsSupplier ? (
          <div>
            <div className="font-medium text-sm">{rowName}</div>
            {self?.customer_group && (
              <div className="text-xs text-muted-foreground">{self.customer_group}</div>
            )}
          </div>
        ) : isSiteRow ? (
          <span className={`text-xs ${isCodeRow ? 'text-muted-foreground' : 'font-medium'}`}>
            {site?.name}
          </span>
        ) : (
          <div className="text-center" title={scope.title}>
            <span className="text-xs">{scope.label}</span>
            {siteCodeLabel(singleSite ?? {}) && (
              <div className="text-[11px] text-muted-foreground">{siteCodeLabel(singleSite ?? {})}</div>
            )}
          </div>
        )}
      </td>

      {/* Rating - Frozen */}
      <td className={`p-3 text-center sticky ${rowBg} z-10 border-r`} style={{ left: `${ratingLeft}px` }}>
        {isNARating(supplier) ? (
          <Badge variant="secondary" className="font-bold">
            N/A
          </Badge>
        ) : (
          <Badge
            className="font-bold"
            style={getRatingStyle(supplier.combined_score ?? undefined, thresholds)}
            title={`Score ${supplier.combined_score?.toFixed(1)}`}
          >
            {getGradeInfo(supplier.combined_score ?? undefined, thresholds).label}
          </Badge>
        )}
      </td>

      {/* Warehouse — CR-4547: only when the tenant shows warehouses (same flag as the header) */}
      {showWarehouse && (
        <td className="p-3 text-center">
          <span className="text-xs">{supplier.warehouse_name || '-'}</span>
        </td>
      )}

      {/* Quality Metrics */}
      <td className="p-3 text-center border-l">
        {count(supplier.qty_received, 'text-primary', 'View GRN details',
          () => onOpenGRNModal(rowId, rowName, 'Qty Received', supplier.qty_received, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.reject_qty, 'text-red-600', 'View NCR details',
          () => onOpenNCRModal(rowId, rowName, 'Qty Rejected', supplier.reject_qty, drillSite))}
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{supplier.customer_liability_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{supplier.supplier_liability_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm font-medium">{supplier.total_ppm?.toFixed(2) || 0}</span>
      </td>
      <td className="p-3 text-center">
        {count(supplier.ncr_count, 'text-primary', 'View NCR details',
          () => onOpenNCRModal(rowId, rowName, 'NCRs', supplier.ncr_count, drillSite))}
      </td>
      <td className="p-3 text-center">
        {(supplier.return_count ?? 0) > 0 ? (
          count(supplier.return_count, 'text-orange-600', 'View Return to Supplier NCRs',
            () => onOpenNCRModal(rowId, rowName, 'Returns', supplier.return_count, drillSite))
        ) : (
          <span className="text-sm">0</span>
        )}
      </td>
      {!viewerIsSupplier && (
        <td className="p-3 text-center">
          <span className="text-sm">{supplier.response_score || 0}</span>
        </td>
      )}

      {/* Cost Metrics */}
      <td className="p-3 text-center border-l">
        <button
          onClick={() => onOpenGRNModal(rowId, rowName, 'Total Value', parseFloat(supplier.total_value || '0'), undefined, drillSite)}
          className="text-sm font-medium text-primary hover:underline cursor-pointer"
          title={`View GRN details${at}`}
        >
          {money(supplier.total_value)}
        </button>
      </td>
      <td className="p-3 text-center">
        <button
          onClick={() => onOpenNCRModal(rowId, rowName, 'Reject Value', parseFloat(supplier.reject_value || '0'), drillSite)}
          className="text-sm text-red-600 hover:underline cursor-pointer"
          title={`View NCR details${at}`}
        >
          {money(supplier.reject_value)}
        </button>
      </td>
      {/* Cost of quality (Customer review 11 Sep, msg 10): calculated figures, no click-through. */}
      <td className="p-3 text-center">
        <span className="text-sm">{money(supplier.customer_cost_of_quality)}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm">{money(supplier.supplier_cost_of_quality)}</span>
      </td>
      <td className="p-3 text-center">
        <span className="text-sm font-medium">{money(supplier.total_cost_of_quality)}</span>
      </td>

      {/* Delivery Metrics (PO Line) */}
      <td className="p-3 text-center border-l">
        {count(supplier.po_line_count, 'text-primary', 'View PO Line details',
          () => onOpenPOLineModal(rowId, rowName, 'PO Lines', supplier.po_line_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.otif_count, 'text-green-600', 'View OTIF details',
          () => onOpenPOLineModal(rowId, rowName, 'OTIF', supplier.otif_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.arrears_count, 'text-red-600', 'View Arrears details',
          () => onOpenPOLineModal(rowId, rowName, 'Arrears', supplier.arrears_count, undefined, drillSite))}
      </td>
      <td className="p-3 text-center">
        <span
          className={`text-sm font-semibold ${supplier.otif_score_percentage == null || otifBands.length === 0 ? 'text-muted-foreground' : ''}`}
          style={supplier.otif_score_percentage != null && otifBands.length > 0
            ? { color: getDeliveryColor(supplier.otif_score_percentage, toDeliveryBands(otifBands), 1) }
            : undefined}
        >
          {supplier.otif_score_percentage == null ? 'N/A' : `${roundHalfUp(supplier.otif_score_percentage, 1).toFixed(1)}%`}
        </span>
      </td>
      <td className="p-3 text-center">
        {count(supplier.qty_diff, 'text-primary', 'View Qty Diff details',
          () => onOpenPOLineModal(rowId, rowName, 'Qty Diff', supplier.qty_diff, undefined, drillSite))}
      </td>

      {/* Delivery Metrics (GRN) */}
      <td className="p-3 text-center border-l">
        {count(supplier.grn_count, 'text-primary', 'View GRN details',
          () => onOpenGRNModal(rowId, rowName, 'No. of GRNs', supplier.grn_count, undefined, drillSite))}
      </td>
      {/* CR-6134: Ontime/Late/Early now open GRN modal filtered by OTD status */}
      <td className="p-3 text-center">
        {count(supplier.ontime_count, 'text-green-600', 'View Ontime deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Ontime Deliveries', supplier.ontime_count, 'on_time', drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.late_count, 'text-red-600', 'View Late deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Late Deliveries', supplier.late_count, 'late', drillSite))}
      </td>
      <td className="p-3 text-center">
        {count(supplier.early_count, 'text-blue-600', 'View Early deliveries (GRN)',
          () => onOpenGRNModal(rowId, rowName, 'Early Deliveries', supplier.early_count, 'early', drillSite))}
      </td>

      {/* CO2 Emissions — Customer review 11 Sep (msg 16): Σ of the CO2 captured
          on the row's ASNs; "—" when none carried a figure, so an absence of
          data never reads as a measured zero. */}
      <td className="p-3 text-center border-l">
        {co2Unknown ? (
          <span className="text-sm text-muted-foreground" title="CO2 is captured per shipment, and a shipment can cover more than one supplier code - so it cannot be split by code. The customer-site row above carries the figure">
            n/a
          </span>
        ) : co2Count === 0 ? (
          <span className="text-sm text-muted-foreground" title="No CO2 captured on this supplier's ASNs in this period">
            —
          </span>
        ) : (
          <span className="text-sm" title={`From ${co2Count.toLocaleString()} ASN${co2Count === 1 ? '' : 's'} with CO2 captured`}>
            {(supplier.carbon_emission || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} kg
          </span>
        )}
      </td>
    </tr>
  );
}
